import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnInit,
  Output,
  Renderer2,
  ViewChild
} from '@angular/core';
import { ICircadianCurve } from '@app/shared/models/circadian-curve.interface';
import * as d3 from 'd3';
import { CircadianCurveService } from '@app/shared/services/circadian-curve.service';
import { CircadianCurve } from '@app/shared/models/circadian-curve';
import { FormsModule, NgForm, ReactiveFormsModule } from '@angular/forms';
import { TagService } from '@app/shared/services/tag.service';
import { MultiselectOption as SelectOptionInterface } from '@app/shared/components/multiselect/multiselect-option';
import { DisplayTags, Tag } from '@app/shared/models/tag.interface';
import { Floor } from '@app/shared/models/floor.interface';
import { NavigationService } from '@app/shared/services/navigation/navigation.service';
import { BehaviorSubject, combineLatestWith, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ToastService } from '@services/toast/toast.service';
import { AsyncPipe } from '@angular/common';
import { MatTooltip } from '@angular/material/tooltip';
import { InlineTagsComponent } from '@components/inline-tags/inline-tags.component';
import { InlineFloorsComponent } from '@components/inline-floors/inline-floors.component';

@Component({
  selector: 'app-form-circadian-curve',
  templateUrl: './form-circadian-curve.component.html',
  styleUrls: ['./form-circadian-curve.component.scss'],
  imports: [ReactiveFormsModule, FormsModule, MatTooltip, AsyncPipe, InlineTagsComponent, InlineFloorsComponent]
})
export class FormCircadianCurveComponent implements OnInit, AfterViewInit {
  @Input() details: ICircadianCurve;
  @Input() curvesList: ICircadianCurve[];
  @Input() isNew: boolean;
  @Output() saveNewCurve = new EventEmitter<ICircadianCurve>();
  @Output() closeNewCurve = new EventEmitter<{}>();
  @Output() resetAndCloseCurve = new EventEmitter<{}>();
  @Output() deleteCurve = new EventEmitter<ICircadianCurve>();

  readonly floors: Floor[] = [];
  @ViewChild('circadianForm') circadianForm: NgForm;
  @ViewChild('circadianCharts') chartRef: ElementRef;
  private readonly buildingId: number;
  private circadianColorChartSelector: string;
  private circadianLightChartSelector: string;
  private colorSvg: d3.Selection<d3.BaseType, unknown, HTMLElement, any>;
  private lightSvg: d3.Selection<d3.BaseType, unknown, HTMLElement, any>;
  private colourTempMin = 2580;
  private colourTempMax = 7140;
  private lightLevelMin = 0;
  private lightLevelMax = 100;
  private availableLightLevels = [0,1,2,3,4,5,6,7,8,9,10,11,12,14,16,18,20,22,25,28,32,36,40,45,50,55,60,70,80,90,100];
  private availableColourTempLevels = [7140,6940,6760,6580,6410,6250,6100,5950,5810,5680,5560,5430,5320,5210,5100,5000,4900,4810,4720,4630,4550,4460,4390,4310,4240,4170,4100,4030,3970,3910,3850,3790,3730,3680,3620,3570,3520,3470,3420,3380,3330,3290,3250,3210,3160,3130,3090,3050,3010,2980,2940,2910,2870,2840,2810,2780,2750,2720,2690,2660,2630,2600,2580];

  isBuildingLevelChecked = false;
  curveId: number;
  readonly margin: { left: number; top: number; right: number; bottom: number };
  width = 0;
  height = 0;
  lightLevels: LightLevel[];
  colourTemps: ColourTemp[];
  x: d3.ScaleLinear<number, number>;
  y0: d3.ScaleLinear<number, number>;
  y1: d3.ScaleLinear<number, number>;
  colourData: number[];
  greyData: number[];
  colourValues: string[];
  greyValues: string[];
  colourTempLine: string;
  lightLevelLine: string;
  colourRects: { x: number; y: number; height: number; width: number; fill: string }[];
  greyRects: { x: number; y: number; height: number; width: number; fill: string }[];
  tagOptions: BehaviorSubject<SelectOptionInterface<Tag>[]> = new BehaviorSubject([]);
  floorOptions: BehaviorSubject<SelectOptionInterface<Floor>[]> = new BehaviorSubject([]);
  tags$: Observable<DisplayTags>;

  constructor(
    private circadianCurveService: CircadianCurveService,
    private tagService: TagService,
    private navigationService: NavigationService,
    private renderer2: Renderer2,
    private cdr: ChangeDetectorRef,
    private toastService: ToastService
  ) {
    this.margin = { top: 20, right: 80, bottom: 50, left: 80 };
    this.height = 500 - this.margin.top - this.margin.bottom;
    this.buildingId = this.navigationService.getActiveSection().routeParams.buildingId;
    this.floors = this.navigationService.getActiveSection().routeParams.floors;
  }

  ngOnInit(): void {
    this.curveId = this.curvesList.indexOf(this.details);
    this.circadianColorChartSelector = 'svg#circadian-color-chart-' + this.curveId;
    this.circadianLightChartSelector = 'svg#circadian-light-chart-' + this.curveId;
    this.updateTagOptions();
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      const divElement = this.chartRef.nativeElement;
      this.setupChart(divElement.clientWidth);
      this.cdr.detectChanges();
    });
  }

  @HostListener('window:resize', ['$event'])
  onResize(resizeEvent: Event): void {
    this.clearGraphAxis();
    const target = resizeEvent.target as Window;
    this.setupChart(target.innerWidth);
  }
  private setupChart(width: number): void {
    this.width = this.getGraphWidth(width);
    this.initAxes();
    this.initColourAndLightValues();
    this.initColourData();
    this.initGreyData();
    this.updateTagOptions();
    this.initChart();
  }

  private roundColourTemps(goal: number): number {
    // https://stackoverflow.com/questions/8584902/get-the-closest-number-out-of-an-array
    return this.availableColourTempLevels.reduce((prev, curr) => {
      return (Math.abs(curr - goal) < Math.abs(prev - goal) ? curr : prev);
    });
  }

  private roundLightLevel(goal: number): number {
    // https://stackoverflow.com/questions/8584902/get-the-closest-number-out-of-an-array
    return this.availableLightLevels.reduce((prev, curr) => {
      return (Math.abs(curr - goal) < Math.abs(prev - goal) ? curr : prev);
    });
  }

  private initAxes(): void {
    // set the ranges
    this.x = d3.scaleLinear().range([0, this.width]);
    this.y0 = d3.scaleLinear().range([this.height, 0]);
    this.y1 = d3.scaleLinear().range([this.height, 0]);
    this.x.domain([0, 23]);
    this.y0.domain([this.colourTempMin, this.colourTempMax]);
    this.y1.domain([this.lightLevelMin, this.lightLevelMax]);
  }

  private static get hoursOfDay(): number[] {
    return new Array(24).fill(0).map((_, i) => i);
  }

  private initChart(): void {
    this.colorSvg = d3.select(this.circadianColorChartSelector);
    this.lightSvg = d3.select(this.circadianLightChartSelector);
    this.drawXAxis();
    this.drawY0Axis();
    this.drawY1Axis();
  }

  // draw Y axis for colour curve
  private drawY0Axis = (): void => {
    const drawGrid = d3.axisLeft(this.y0).tickSize(-this.width);

    this.colorSvg.select('#y0-axis').append('g').attr('class', 'axisSteelBlue').call(drawGrid);

    this.colorSvg.selectAll('#y0-axis g.tick line').attr('stroke', '#def');

    this.colorSvg
      .select('#y0-axis')
      .append('text')
      .attr('transform', 'rotate(-90)')
      .attr('y', 0 - this.margin.left + 15)
      .attr('x', 0 - this.height / 2)
      .attr('dy', '1em')
      .style('text-anchor', 'middle')
      .text('Colour Temperature (Kelvin)');
  };

  // draw Y axis for light curve
  private drawY1Axis = (): void => {
    const drawGrid = d3.axisLeft(this.y1).tickSize(-this.width);

    this.lightSvg.select('#y1-axis').append('g').attr('class', 'axisRed').call(drawGrid);

    this.lightSvg.selectAll('#y1-axis g.tick line').attr('stroke', '#def');

    this.lightSvg
      .select('#y1-axis')
      .append('text')
      .attr('transform', 'rotate(-90)')
      .attr('y', 0 - this.margin.left + 30)
      .attr('x', 0 - this.height / 2)
      .attr('dy', '1em')
      .style('text-anchor', 'middle')
      .text('Light Level (%)');
  };

  private drawXAxis = (): void => {
    const xAxisGenerator = d3.axisBottom(this.x).ticks(FormCircadianCurveComponent.hoursOfDay.length);
    // Add X Axis to color curve
    this.colorSvg
      .select('#x-axis')
      .append('g')
      .attr('transform', 'translate(0,' + this.height + ')')
      .call(xAxisGenerator);

    // text label for the x-axis
    this.colorSvg
      .select('#x-axis')
      .append('text')
      .attr('transform', 'translate(' + this.width / 2 + ' ,' + (this.height + this.margin.top + 20) + ')')
      .style('text-anchor', 'middle')
      .text('Hour');

    // Add X Axis to light curve
    this.lightSvg
      .select('#x-axis')
      .append('g')
      .attr('transform', 'translate(0,' + this.height + ')')
      .call(xAxisGenerator);

    // text label for the x-axis
    this.lightSvg
      .select('#x-axis')
      .append('text')
      .attr('transform', 'translate(' + this.width / 2 + ' ,' + (this.height + this.margin.top + 20) + ')')
      .style('text-anchor', 'middle')
      .text('Hour');
  };

  private createTemperatureLine = (data): string =>
    d3
      .line<{ time: number; colour_temp: number }>()
      .x((d) => this.x(d.time))
      .y((d) => this.y0(d.colour_temp))(data);

  private createLightLine = (data): string =>
    d3
      .line<{ time: number; light_level: number }>()
      .x((d) => this.x(d.time))
      .y((d) => this.y1(d.light_level))(data);

  private initColourAndLightValues = (): void => {
    this.colourTemps = FormCircadianCurveComponent.hoursOfDay.map((t, i) => {
      return { time: t, colour_temp: this.roundColourTemps(this.details.colourTempValues[i]) };
    });

    this.lightLevels = FormCircadianCurveComponent.hoursOfDay.map((t, i) => {
      return { time: t, light_level: this.roundLightLevel(this.details.lightLevelValues[i]) };
    });

    this.colourTempLine = this.createTemperatureLine(this.colourTemps);
    this.lightLevelLine = this.createLightLine(this.lightLevels);
  };

  private initColourData(): void {
    this.colourData = d3.range(this.height);
    this.colourValues = d3.quantize(d3.interpolateRdYlBu, this.colourData.length).reverse();
    this.colourRects = this.colourData.map((val) => {
      const y = val * (this.height / this.colourData.length);
      const height = this.height / this.colourData.length;
      const x = 0;
      const width = 20;
      const fill = this.colourValues[val];
      return {
        y,
        x,
        height,
        width,
        fill
      };
    });
  }

  private initGreyData(): void {
    this.greyData = d3.range(this.height);
    this.greyValues = d3.quantize(d3.interpolateGreys, this.greyData.length);
    this.greyRects = this.greyData.map((val) => {
      const y = val * (this.height / this.greyData.length);
      const height = this.height / this.greyData.length;
      const x = 0;
      const width = 20;
      const fill = this.greyValues[val];
      return {
        y,
        x,
        height,
        width,
        fill
      };
    });
  }

  onMouseDown = (lineType: LineType, colourTempCircle: LightLevel | ColourTemp): void => {
    const chartTop = (this.colorSvg.node() as any).getBoundingClientRect().top;
    const valuesArray = lineType === LineType.COLOR_TEMP ? this.colourTemps : this.lightLevels;
    const yScale = lineType === LineType.COLOR_TEMP ? this.y0 : this.y1;
    const unlistenMouseMove = this.renderer2.listen('document', 'mousemove', (e: MouseEvent) => {
      if (lineType === LineType.COLOR_TEMP) {
        const colourTemp = d3.max([
          this.colourTempMin,
          d3.min([yScale.invert(e.clientY - chartTop - this.margin.top), this.colourTempMax])
        ]);
        valuesArray[colourTempCircle.time] = {
          time: valuesArray[colourTempCircle.time].time,
          colour_temp: this.roundColourTemps(colourTemp)
        };
        this.colourTempLine = this.createTemperatureLine(valuesArray);
      } else {
        const lightLevel = d3.max([
          this.lightLevelMin,
          d3.min([yScale.invert(e.clientY - chartTop - this.margin.top), this.lightLevelMax])
        ]);
        valuesArray[colourTempCircle.time] = {
          time: valuesArray[colourTempCircle.time].time,
          light_level: this.roundLightLevel(lightLevel)
        };
        this.lightLevelLine = this.createLightLine(valuesArray);
      }
    });
    const unlistenMouseUp = this.renderer2.listen('document', 'mouseup', () => {
      unlistenMouseMove();
      unlistenMouseUp();
    });
  };

  private updateTagOptions(): void {
    this.tags$ = this.tagService.getTagsForBuildingId(this.buildingId, true).pipe(
      combineLatestWith(this.tagService.getTagsForBuildingId(this.buildingId, false)),
      map((tags) => {
        return {
          displayTags: tags[1],
          tags: tags[0]
        };
      })
    );
  }

  public selectedTagsChanged(newTags: Tag[]): void {
    this.details.tags = newTags.map((tag) => ({ ...tag, buildingId: this.buildingId }));
  }

  public selectedFloorsChanged(newFloors: Floor[]): void {
    this.details.floors = newFloors.map((floor) => ({ ...floor, buildingId: this.buildingId }));
  }

  public save(): void {
    this.details.colourTempValues = this.colourTemps.map((v) => v.colour_temp);
    this.details.lightLevelValues = this.lightLevels.map((v) => v.light_level);
    if (this.details.id != null) {
      this.circadianCurveService.updateCurve(this.details).subscribe({
        next: (curve) => {
          if (this.details.active) {
            alert(
              'Please note that any changes to the light level or colour temperature made in an active curve will take some time to take effect'
            );
          }
          this.details = CircadianCurve.clone(curve);
          this.close();
        },
        error: (e) => {
          if (e.status === 400) {
            const { message } = e.error;
            this.toastService.error({
              message: `Unable to update curve: ${message}`,
              dataCy: 'circadian-save-toast-error'
            });
          }
        }
      });
    } else {
      this.circadianCurveService.saveCurve(this.buildingId, this.details).subscribe({
        next: (curve) => {
          this.details = CircadianCurve.clone(curve);
          this.saveNewCurve.emit(this.details);
        },
        error: (e) => {
          if (e.status === 400) {
            const { message } = e.error;
            this.toastService.error({
              message: `Unable to create curve: ${message}`,
              dataCy: 'circadian-update-toast-error'
            });
          }
        }
      });
    }
  }

  public delete(): void {
    if (!confirm(`Delete curve named: '${this.details.name}?'`)) {
      return;
    }

    if (this.details.id != null) {
      this.circadianCurveService.deleteCurve(this.details).subscribe(() => {
        this.deleteCurve.emit(this.details);
      });
    }
  }

  public close(): void {
    if (this.isNew) {
      this.closeNewCurve.emit();
    } else {
      this.resetAndCloseCurve.emit();
    }
  }

  public getFloorIds(): number[] {
    return this.details.floors.map((floor) => floor.id);
  }

  public getTagIds(): number[] {
    return this.details.tags.map((tag) => tag.id);
  }

  private getGraphWidth(windowWidth: number): number {
    if (windowWidth > 1200) {
      return windowWidth / 2 - windowWidth * 0.04 - this.margin.left - this.margin.right; // 0.04 factor is to avoid svg moving out from the base container scope
    } else {
      return windowWidth - windowWidth * 0.15 - this.margin.left - this.margin.right; // 0.15 factor is to avoid svg moving out from the base container scope
    }
  }

  private clearGraphAxis(): void {
    d3.selectAll('#x-axis g').remove();
    d3.selectAll('#x-axis text').remove();
    d3.selectAll('#y0-axis g').remove();
    d3.selectAll('#y0-axis text').remove();
    d3.selectAll('#y1-axis g').remove();
    d3.selectAll('#y1-axis text').remove();
  }
}

interface LightLevel {
  time: number;
  light_level: number;
}

interface ColourTemp {
  time: number;
  colour_temp: number;
}

enum LineType {
  COLOR_TEMP = 1,
  LIGHT_LEVEL
}
